import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AppRoutes } from 'routes/routeConstants/appRoutes';
import { PrivateRouteProps } from 'enums/PrivateRouteEnums';
import { hasAdminAccess } from 'shared/utils/roleUtils';

interface RoleProtectedRouteProps {
  children: JSX.Element;
  /**
   * Function that checks if the user has access based on their role
   * Returns true if access is granted, false otherwise
   */
  hasAccess: (role?: string) => boolean;
  /**
   * Route to redirect to if access is denied
   * Defaults to dashboard
   */
  redirectTo?: string;
}

/**
 * RoleProtectedRoute component
 * 
 * Protects routes based on user role. Only renders children if the user
 * has the required role access. Otherwise redirects to the specified route.
 * 
 * @example
 * <RoleProtectedRoute hasAccess={hasAdminAccess}>
 *   <AccountsList />
 * </RoleProtectedRoute>
 */
const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({
  children,
  hasAccess,
  redirectTo = AppRoutes.DASHBOARD,
}) => {
  const location = useLocation();
  const userDetails = localStorage.getItem(PrivateRouteProps.USER)
    ? JSON.parse(localStorage.getItem('userDetails') ?? '{}')
    : null;

  const userRole = userDetails?.role;

  useEffect(() => {
    if (!hasAccess(userRole)) {
      console.warn(
        `Access denied to ${location.pathname}. User role: ${userRole || 'undefined'}`
      );
    }
  }, [userRole, location.pathname, hasAccess]);

  if (!userDetails) {
    return <Navigate to={AppRoutes.LOGIN_VIEW} replace />;
  }

  if (!hasAccess(userRole)) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

export default RoleProtectedRoute;
